import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { MaskText, FadeUp } from "./MaskText";
import { useLocale } from "@/i18n/LocaleContext";
import { WORK } from "@/i18n/translations";

export const ForBusiness = () => {
  const { t } = useLocale();
  const b = t.business;

  return (
    <section id="business" className="relative border-t border-white/10 py-24 md:py-32" data-testid="business-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <FadeUp>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-nowi-venture">{b.kicker}</span>
            </FadeUp>
            <MaskText
              lines={b.heading}
              className="mt-5 font-playfair text-4xl md:text-6xl leading-[0.95] tracking-[-0.02em]"
            />
          </div>
          <FadeUp delay={0.1}>
            <p className="max-w-md text-nowi-muted text-sm md:text-base">{b.subtitle}</p>
          </FadeUp>
        </div>

        <div className="grid lg:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {/* What you get */}
          <div className="bg-nowi-bg p-8 md:p-12 flex flex-col justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-8">{b.includedTitle}</div>
              <ul className="space-y-5">
                {b.included.map((item, i) => (
                  <motion.li
                    key={i}
                    data-testid={`business-included-${i}`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: i * 0.06 }}
                    className="flex items-start gap-4"
                  >
                    <Check className="w-5 h-5 mt-0.5 text-nowi-venture shrink-0" strokeWidth={1.5} />
                    <span className="text-nowi-text text-base md:text-lg font-playfair">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            <a
              href="#contact"
              data-testid="business-cta"
              className="mt-12 inline-flex items-center gap-3 bg-nowi-venture text-nowi-bg px-8 py-4 text-xs font-mono uppercase tracking-[0.15em] hover:bg-nowi-text transition-colors duration-300 w-fit"
            >
              {b.cta} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>

          {/* Featured live client — Bernedotter */}
          <a
            href={WORK.bernedotterUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="featured-work-bernedotter"
            className="group relative bg-nowi-surface overflow-hidden min-h-[420px] flex flex-col justify-end"
          >
            <div className="absolute inset-0">
              <motion.img
                src={WORK.bernedotterImage}
                alt={b.featuredName}
                className="w-full h-full object-cover opacity-55 group-hover:opacity-70 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-nowi-bg via-nowi-bg/60 to-transparent" />
            </div>

            <div className="relative z-10 p-8 md:p-12">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nowi-venture mb-4">{b.featuredLabel}</div>
              <h3 className="font-playfair text-4xl md:text-5xl mb-2">{b.featuredName}</h3>
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-nowi-muted mb-5">{b.featuredSector}</div>
              <p className="text-nowi-muted text-sm max-w-md mb-6">{b.featuredDesc}</p>
              <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-nowi-text border-b border-nowi-venture pb-1">
                {b.viewLive} — bernedotter.se
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" strokeWidth={1.5} />
              </span>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
};
